import "./MissionsFilters.scss";
import Button from "core/buttons/button/Button";
import ClearFiltersButton from "core/buttons/clear-filters-button/ClearFiltersButton";
import FromToInputDate from "core/inputs/input-date/FromToInputDate";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputText from "core/inputs/input-text/InputText";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage } from "react-intl";
import {
  FILTERS_FOR_NAMES,
  MISSION_STATUS_FILTERS,
  TAG_TYPES,
} from "constants/constants";
import { Typography } from "core/typography/Typography";
import {
  formatClientOptions,
  formatNames,
  formatNamesByFilters,
} from "../CommonInputs";
import { useContext } from "react";
import InfoContext from "pages/InfoContext";
import PropTypes from "prop-types";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";

const MissionsFilters = ({ search, filters, setFilters }) => {
  const {
    lang,
    branchList,
    getClients,
    clientsList,
    setIsTouched,
    displayMission,
    setDisplayMission,
    setMissionsByFilters,
    setCurrentPage,
    fetchNamesForFilters,
    namesForFilters,
    initDisplayMission,
    isFromSpreadsheetPage,
    navigate,
    userScope,
  } = useContext(InfoContext);
  const {
    status,
    workers,
    clients,
    missionTitle,
    managers,
    startFromDate,
    endFromDate,
    startToDate,
    endToDate,
    signedBy,
    chargedBy,
  } = filters;

  const hasActiveFilters = () => {
    return (
      workers?.length > 0 ||
      clients?.length > 0 ||
      missionTitle?.length > 0 ||
      managers?.length > 0 ||
      startFromDate?.length > 0 ||
      endFromDate?.length > 0 ||
      startToDate?.length > 0 ||
      endToDate?.length > 0 ||
      signedBy?.length > 0 ||
      chargedBy?.length > 0 ||
      status?.length > 0
    );
  };

  const clearAllFilters = () => {
    setFilters((prev) => ({
      ...prev,
      workers: [],
      clients: [],
      missionTitle: "",
      managers: [],
      startFromDate: "",
      endFromDate: "",
      startToDate: "",
      endToDate: "",
      signedBy: [],
      chargedBy: [],
      status: [],
    }));
  };
  return (
    <div className="section-1 missions-filters-wrapper">
      <div>
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={<FormattedMessage id="filters" defaultMessage="Filtres" />}
        />
        <div className="missions-filters">
          <div className="center-block">
            <div className="line-1">
              <div className="cell-padding">
                <InputSearchTag
                  label={{
                    id: "status",
                    defaultMessage: "Statuts",
                  }}
                  popup={FORM_INFO.statusFilter.popup}
                  limitChoice={4}
                  options={MISSION_STATUS_FILTERS}
                  errors={{}}
                  selectedOptions={status}
                  setValue={(val) => {
                    setIsTouched(true);
                    setFilters((prev) => ({ ...prev, status: val }));
                  }}
                  onDelete={(selected) => {
                    setIsTouched(true);
                    setFilters((prev) => ({
                      ...prev,
                      status: prev.status?.filter(
                        (item) => item.value !== selected
                      ),
                    }));
                  }}
                  id="status-filter"
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  errors={{}}
                  getOptions={(search) =>
                    fetchNamesForFilters({
                      profile: ["str", "cslt"],
                      types: [FILTERS_FOR_NAMES.PERSON],
                      search,
                    })
                  }
                  label={{
                    id: "filter_worker",
                    defaultMessage: "Consultants / Sous-traitants",
                  }}
                  limitChoice={10}
                  minLength={1}
                  onDelete={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      workers: prev.workers?.filter(
                        (item) => item.value !== selected
                      ),
                    }))
                  }
                  options={formatNamesByFilters(namesForFilters) || []}
                  popup={FORM_INFO.workerFilter.popup}
                  selectedOptions={workers}
                  setValue={(val) =>
                    setFilters((prev) => ({ ...prev, workers: val }))
                  }
                  type={TAG_TYPES.CSLT_STR}
                  id="worker-filter"
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  errors={{}}
                  getOptions={getClients}
                  label={{
                    id: "clients",
                    defaultMessage: "Clients",
                  }}
                  limitChoice={100}
                  minLength={1}
                  onDelete={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      clients: prev.clients?.filter(
                        (item) => item.value !== selected
                      ),
                    }))
                  }
                  options={formatClientOptions(clientsList)}
                  popup={FORM_INFO.clientFilter.popup}
                  selectedOptions={clients}
                  setValue={(val) =>
                    setFilters((prev) => ({ ...prev, clients: val }))
                  }
                  type={TAG_TYPES.CLIENTS}
                  id="client-filter"
                />
              </div>
              <div className="cell-padding">
                <InputText
                  errors={{}}
                  label={{
                    id: "filter_mission_title",
                    defaultMessage: "Intitulé de la mission",
                  }}
                  placeholder={{
                    id: "filter_mission_title_placeholder",
                    defaultMessage: '"PO" OR "Product Owner" AND Scrum',
                  }}
                  id="mission-title-filter"
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      missionTitle: e.target.value,
                    }))
                  }
                  value={missionTitle}
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  errors={{}}
                  getOptions={(search) =>
                    fetchNamesForFilters({
                      role: MISSION_MANAGER_ROLES,
                      types: [FILTERS_FOR_NAMES.PERSON],
                      search,
                    })
                  }
                  label={{
                    id: "filter_managers",
                    defaultMessage: "Managers",
                  }}
                  limitChoice={19}
                  minLength={1}
                  onDelete={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      managers: prev.managers?.filter(
                        (item) => item.value !== selected
                      ),
                    }))
                  }
                  options={formatNamesByFilters(namesForFilters)}
                  popup={FORM_INFO.managerFilter.popup}
                  selectedOptions={managers}
                  setValue={(val) =>
                    setFilters((prev) => ({ ...prev, managers: val }))
                  }
                  type={TAG_TYPES.PEOPLE}
                  id="manager-filter"
                />
              </div>
            </div>
            <div className="line-2">
              <div className="cell-padding">
                <FromToInputDate
                  idFrom={"start-from-date-filter"}
                  idTo={"start-to-date-filter"}
                  label={{
                    id: "start_date_filter",
                    defaultMessage: "Date de début de contrat",
                  }}
                  errors={{}}
                  lang={lang}
                  onChangeFrom={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      startFromDate: e?.target?.value,
                    }))
                  }
                  onChangeTo={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      startToDate: e?.target?.value,
                    }))
                  }
                  valueFrom={startFromDate}
                  valueTo={startToDate}
                  readOnly={false}
                />
              </div>
              <div className="cell-padding">
                <FromToInputDate
                  idFrom={"end-from-date-filter"}
                  idTo={"end-to-date-filter"}
                  label={{
                    id: "end_date_filter",
                    defaultMessage: "Date de fin de contrat",
                  }}
                  errors={{}}
                  lang={lang}
                  onChangeFrom={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      endFromDate: e?.target?.value,
                    }))
                  }
                  onChangeTo={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      endToDate: e?.target?.value,
                    }))
                  }
                  valueFrom={endFromDate}
                  valueTo={endToDate}
                  readOnly={false}
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  label={{
                    id: "signed_by_filter",
                    defaultMessage: "Affaires signées par",
                  }}
                  limitChoice={10}
                  onDelete={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      signedBy: prev.signedBy?.filter(
                        (item) => item.value !== selected
                      ),
                    }))
                  }
                  options={formatNames(branchList)}
                  popup={FORM_INFO.branchFilter.popup}
                  selectedOptions={signedBy}
                  setValue={(val) =>
                    setFilters((prev) => ({ ...prev, signedBy: val }))
                  }
                  type={TAG_TYPES.BRANCHES}
                  errors={{}}
                  id="signed-by-filter"
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  label={{
                    id: "billed_by_filter",
                    defaultMessage: "Affaires facturées par",
                  }}
                  limitChoice={10}
                  onDelete={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      chargedBy: prev.chargedBy?.filter(
                        (item) => item.value !== selected
                      ),
                    }))
                  }
                  options={formatNames(branchList)}
                  popup={FORM_INFO.branchFilter.popup}
                  selectedOptions={chargedBy}
                  setValue={(val) =>
                    setFilters((prev) => ({ ...prev, chargedBy: val }))
                  }
                  type={TAG_TYPES.BRANCHES}
                  errors={{}}
                  id="billed-by-filter"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="div-buttons">
        <Button
          color="gray-700"
          size="medium"
          onClick={() => {
            setDisplayMission(false);
            setMissionsByFilters({});
            setCurrentPage(1);
            search(1);
          }}
          id="button-search"
        >
          <FormattedMessage id="search_button" defaultMessage="Rechercher" />
        </Button>
        <ClearFiltersButton
          onClear={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
        />
        {displayMission && (
          <Typography
            className="link-1 violet "
            type="h2"
            onClick={() =>
              isFromSpreadsheetPage
                ? navigate(`/${userScope}/inputs/spreadsheet`, {
                    state: { isFromMissionDetail: true },
                  })
                : initDisplayMission()
            }
            label={
              <FormattedMessage
                id={`${
                  isFromSpreadsheetPage
                    ? "return_spreadsheet"
                    : "return_results"
                }`}
                defaultMessage={`${
                  isFromSpreadsheetPage
                    ? "Retour tableau de saisie"
                    : "Revenir aux résultats"
                }`}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

export default MissionsFilters;

MissionsFilters.propTypes = {
  filters: PropTypes.object,
  search: PropTypes.func,
  setFilters: PropTypes.func,
};
